import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { EditionMeta } from "../types";

const familyLabels: Record<string, string> = {
  personal: "Personal",
  business: "Business",
  isa: "ISA",
  charity: "Charity",
};

export default function Editions() {
  const [params] = useSearchParams();
  const family = params.get("family") ?? "personal";
  const [editions, setEditions] = useState<EditionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function refresh() {
    setLoading(true);
    try {
      const { editions } = await api.listEditions(family);
      setEditions(editions);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [family]);

  async function handleNew() {
    setBusy(true);
    try {
      const { edition } = await api.createEdition(family);
      nav(`/editions/${edition.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDuplicate(id: string) {
    setBusy(true);
    try {
      const { edition } = await api.createEdition(family, { cloneFromId: id });
      nav(`/editions/${edition.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this draft?")) return;
    await api.deleteEdition(id);
    refresh();
  }

  const supported = family === "personal" || family === "business";

  return (
    <div className="px-10 py-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{familyLabels[family] ?? family} guides</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Drafts and published editions.
          </p>
        </div>
        {supported && (
          <button
            onClick={handleNew}
            disabled={busy}
            className="rounded bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800 disabled:opacity-50"
          >
            {busy ? "Creating…" : "New edition"}
          </button>
        )}
      </div>

      {!supported ? (
        <div className="rounded border border-dashed p-10 text-center text-neutral-500 text-sm">
          {familyLabels[family] ?? family} guides are coming in a later phase.
        </div>
      ) : loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : editions.length === 0 ? (
        <div className="rounded border border-dashed p-10 text-center text-neutral-500 text-sm">
          No editions yet. Click <em>New edition</em> to create one from defaults.
        </div>
      ) : (
        <div className="rounded-lg border bg-white divide-y">
          {editions.map((e) => (
            <div
              key={e.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-neutral-50"
            >
              <button
                onClick={() => nav(`/editions/${e.id}`)}
                className="flex-1 text-left min-w-0"
              >
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium truncate">
                    {e.version_code || "Untitled"}
                  </span>
                  <StatusPill status={e.status} />
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {e.effective_date && <>Effective {e.effective_date} · </>}
                  Updated {new Date(e.updated_at + "Z").toLocaleString()}
                </div>
              </button>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => handleDuplicate(e.id)}
                  className="text-xs text-neutral-600 hover:text-neutral-900 underline"
                >
                  Duplicate
                </button>
                {e.status === "draft" && (
                  <button
                    onClick={() => handleDelete(e.id)}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: "draft" | "published" }) {
  const styles =
    status === "published"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-amber-100 text-amber-800";
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-2 py-0.5 ${styles}`}>
      {status}
    </span>
  );
}
